import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react'
import AceEditor from 'react-ace'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ErrorBoundary from '../../../../molecules/ErrorBoundary'
import { dataCatalog } from '../../../../../api/coreData'

// Import Ace Editor modules for MySQL syntax highlighting and autocomplete
import 'ace-builds/src-noconflict/mode-mysql'
import 'ace-builds/src-noconflict/theme-xcode'
import 'ace-builds/src-noconflict/ext-language_tools'

const useStyles = makeStyles((theme) => ({
  editorPanel: {
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '40px 1fr auto',
    gap: '0px 0px',
    gridTemplateAreas: '"editor-header" "editor-body" "editor-footer"',
    '& .__editor-header': {
      gridArea: 'editor-header'
    },
    '& .__editor-body': {
      gridArea: 'editor-body'
    },
    '& .__editor-footer': {
      gridArea: 'editor-footer'
    },
    backgroundColor: 'var(--surface-bg-color-d4)'
  }
}))

const EditorPanel = forwardRef(({ defaultValue, onSubmitQuery, processing, onChange, onSave, toolbar, footer }, ref) => {
  const classes = useStyles()
  const editorRef = useRef()
  const completerRef = useRef(null)

  // Fetch schema data for autocomplete - don't block editor if not loaded
  const { data: schemaResponse } = dataCatalog.useListCatalogSchema({
    includeUnpublished: false
  })

  // Check for query passed from AI Assistant
  const [initialValue] = useState(() => {
    const assistantQuery = sessionStorage.getItem('denaliAssistantQuery')
    if (assistantQuery) {
      // Clear it so it doesn't persist
      sessionStorage.removeItem('denaliAssistantQuery')
      sessionStorage.removeItem('denaliAssistantQueryType')
      return assistantQuery
    }
    return defaultValue
  })

  // Keep the underlying editor in sync when defaultValue changes, without
  // overwriting any existing user input. This ensures loading a saved query
  // (for example via ?queryId=...) updates a blank editor.
  useEffect(() => {
    if (!editorRef.current || !editorRef.current.editor) return
    const editor = editorRef.current.editor
    const currentValue = editor.getValue()

    // Only apply the new defaultValue if the editor is currently empty,
    // so we don't clobber any user edits.
    if (!currentValue && defaultValue) {
      editor.setValue(defaultValue || '', -1)
    }
  }, [defaultValue])

  // Expose insertText, getValue, and setValue methods via ref
  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      if (editorRef.current && editorRef.current.editor) {
        const editor = editorRef.current.editor
        editor.session.insert(editor.getCursorPosition(), text)
        editor.focus()
      }
    },
    getValue: () => {
      if (editorRef.current && editorRef.current.editor) {
        return editorRef.current.editor.getValue()
      }
      return ''
    },
    setValue: (text) => {
      if (editorRef.current && editorRef.current.editor) {
        editorRef.current.editor.setValue(text, -1)
        editorRef.current.editor.focus()
      }
    }
  }), [])

  useEffect(() => {
    // Add custom completions for OQL schemas and common tables
    const langTools = window.ace.require('ace/ext/language_tools')

    // Store completer in ref so we can remove it on cleanup
    completerRef.current = {
      getCompletions: function (editor, session, pos, prefix, callback) {
        const completions = []

        // If we have schema data from API, use it
        if (schemaResponse?.catalogViews) {
          // Extract unique schemas and add them as completions
          const schemaSet = new Set()
          const tablesBySchema = {}

          schemaResponse.catalogViews.forEach(view => {
            const schemaName = view.viewType || 'unknown'
            schemaSet.add(schemaName)

            if (!tablesBySchema[schemaName]) {
              tablesBySchema[schemaName] = []
            }
            tablesBySchema[schemaName].push({
              name: view.name || view.viewId,
              viewId: view.viewId,
              tableName: view.tableName,
              description: view.description,
              columns: view.configuration?.columns || {}
            })
          })

          // Add schema completions
          Array.from(schemaSet).forEach(schema => {
            completions.push({
              caption: schema,
              value: schema + '.',
              meta: 'schema',
              score: 1000,
              docHTML: `<b>${schema}</b> schema`
            })
          })

          // Add table completions with descriptions
          Object.entries(tablesBySchema).forEach(([schema, tables]) => {
            tables.forEach(table => {
              // Use viewId as the primary identifier
              completions.push({
                caption: table.viewId,
                value: table.viewId,
                meta: `${schema} table`,
                score: 950,
                docHTML: table.description
                  ? `<b>${table.name}</b><br/><i>${table.description}</i>`
                  : `<b>${table.name}</b>`
              })

              // Also add with schema prefix
              completions.push({
                caption: `${schema}.${table.viewId}`,
                value: `${schema}.${table.viewId}`,
                meta: 'table',
                score: 900,
                docHTML: table.description
                  ? `<b>${table.name}</b><br/><i>${table.description}</i>`
                  : `<b>${table.name}</b>`
              })

              // Add column completions for this table
              Object.entries(table.columns).forEach(([columnName, columnDef]) => {
                completions.push({
                  caption: columnName,
                  value: columnName,
                  meta: `${columnDef.type} (${table.viewId})`,
                  score: 850,
                  docHTML: `<b>${columnDef.title || columnName}</b> - ${table.name}<br/>` +
                           `<i>${columnDef.description || 'No description'}</i>` +
                           (columnDef.pii ? '<br/><span style="color: orange">⚠️ PII</span>' : '')
                })

                // Also add table.column format
                completions.push({
                  caption: `${table.viewId}.${columnName}`,
                  value: `${table.viewId}.${columnName}`,
                  meta: columnDef.type,
                  score: 840,
                  docHTML: `<b>${columnDef.title || columnName}</b><br/>` +
                           `<i>${columnDef.description || 'No description'}</i>` +
                           (columnDef.pii ? '<br/><span style="color: orange">⚠️ PII</span>' : '')
                })
              })
            })
          })
        }

        // Common SQL/OQL keywords - always include these
        const keywords = [
          'SELECT', 'FROM', 'WHERE', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN',
          'INNER JOIN', 'ON', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT',
          'AS', 'AND', 'OR', 'IN', 'NOT', 'NULL', 'BETWEEN', 'LIKE',
          'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'DISTINCT', 'WITH',
          'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'UNION', 'ALL'
        ]

        keywords.forEach(keyword => {
          completions.push({
            caption: keyword,
            value: keyword,
            meta: 'keyword',
            score: 800
          })
        })

        callback(null, completions)
      }
    }

    // Add our custom completer to the existing completers
    langTools.addCompleter(completerRef.current)

    // Clean up on unmount
    return () => {
      try {
        // Remove our custom completer from the completers array
        const langTools = window.ace.require('ace/ext/language_tools')
        if (langTools && langTools.completers && Array.isArray(langTools.completers) && completerRef.current) {
          const index = langTools.completers.indexOf(completerRef.current)
          if (index > -1) {
            langTools.completers.splice(index, 1)
          }
        }
      } catch (e) {
        // Ignore cleanup errors - component is unmounting
      }
    }
  }, [schemaResponse])

  const handleSubmitQuery = useCallback(() => {
    if (!onSubmitQuery || processing) return

    onSubmitQuery(editorRef.current.editor.getValue())
  }, [onSubmitQuery, processing])

  const handleEditorLoad = useCallback((editor) => {
    // Add keyboard shortcut for submitting query
    editor.commands.addCommand({
      name: 'submitQuery',
      bindKey: {
        win: 'Ctrl-Enter',
        mac: 'Cmd-Enter'
      },
      exec: handleSubmitQuery,
      readOnly: true
    })

    // Add keyboard shortcut for saving query
    if (onSave) {
      editor.commands.addCommand({
        name: 'saveQuery',
        bindKey: {
          win: 'Ctrl-S',
          mac: 'Cmd-S'
        },
        exec: () => onSave(),
        readOnly: true
      })
    }
  }, [handleSubmitQuery, onSave])

  return (
    <ErrorBoundary name='Code Editor'>
      <div className={classes.editorPanel}>
        <div className='__editor-header'>
          {toolbar}
        </div>
        <div className='__editor-body'>
          <AceEditor
            ref={editorRef}
            height='100%'
            width='100%'
            mode='mysql'
            theme='xcode'
            name='sql-editor'
            editorRef={{ $blockScroll: true }}
            defaultValue={initialValue}
            tabSize={2}
            fontSize={14}
            showPrintMargin={false}
            showGutter
            highlightActiveLine
            onLoad={handleEditorLoad}
            onChange={onChange}
            setOptions={{
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: true,
              enableSnippets: true,
              showLineNumbers: true,
              tabSize: 2,
              useWorker: false
            }}
          />
        </div>
        {footer && (
          <div className='__editor-footer'>
            {footer}
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
})

EditorPanel.displayName = 'EditorPanel'

EditorPanel.propTypes = {
  defaultValue: PropTypes.string,
  onSubmitQuery: PropTypes.func,
  processing: PropTypes.bool,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  toolbar: PropTypes.node,
  footer: PropTypes.node
}

EditorPanel.defaultProps = {
  defaultValue: ''
}

export default EditorPanel
